import { useEffect, useState, type JSX } from 'react'
import {
  api,
  shortDate,
  stampWithZone,
  type IncidentBreach,
  type IncidentBreachPage,
  type IncidentBreachRow,
} from '../../lib/api'
import { Status, type Tone } from '../kit'
import { StatusChip } from '../StatusChip'

// What became of the vendor's incidents in FOCTTA Consent (release 3.3, queue
// item 40; contract docs/interop/vendor-registry-v1.md section 11.5).
//
// A vendor incident recorded here reaches Consent, where the DPO opens it as a
// suspected personal data breach and decides what it is. Consent tells this
// product each step back. Two places show it:
//
//  - VendorIncidentBreachBox, on the Exposure part: each incident with its
//    state in Consent, in plain words.
//  - VendorBreachRiskInput, on the Risk part: one line when an incident in the
//    last 12 months is a suspected or confirmed breach. It is an input shown to
//    the reader; the computed score does not count it.
//
// Without Consent the box says so once, in one line, because a column of
// blanks would read as "not a breach", which nobody has decided. A role that
// is not shown the DPO's decisions (docs/DECISIONS.md, 12 October 2026) is
// told so once. A read that fails says so, in both places: a box that quietly
// disappeared would read as a vendor with no incidents.

const DISMISSED_BECAUSE: Record<string, string> = {
  NO_BREACH: 'no breach',
  DUPLICATE: 'duplicate',
  OUT_OF_SCOPE: 'out of scope',
  MALICIOUS: 'malicious report',
  SPAM: 'spam',
}

// The breach statuses that mean a breach was confirmed.
const CONFIRMED = new Set(['CONFIRMED', 'NOTIFIED', 'CLOSED'])

const RETRY_HINT = 'Retry it under Settings, Vendor registry.'

function toolName(b: IncidentBreach, long = false): string {
  if (b.via === 'REGISTRY_KEY') return 'the breach tool'
  return long ? 'FOCTTA Consent' : 'Consent'
}

// One incident's state in Consent: a short chip (the kit's chips do not wrap),
// and where there is more to say, a line under it.
type ConsentState = { tone: Tone; text: string; note?: string }

// The words and tone for one breach statement.
function breachWords(b: IncidentBreach): ConsentState {
  const tool = toolName(b)
  const ref = b.breachReference ?? b.reportReference
  switch (b.status) {
    case 'SUSPECTED':
      return { tone: 'warn', text: 'Suspected breach in ' + tool + ' (' + b.reportReference + ')' }
    case 'CONFIRMED':
      return { tone: 'crit', text: 'Confirmed as a personal data breach (' + ref + ')' }
    case 'DISMISSED':
      return {
        tone: 'neutral',
        text: 'Dismissed in ' + tool + ': ' + (DISMISSED_BECAUSE[b.dismissReason ?? ''] ?? 'no reason given'),
      }
    case 'NOTIFIED': {
      // The Board and the people affected are statutory facts: the time, with
      // its zone, not a day that can move with the reader's clock.
      const told: string[] = []
      if (b.boardNotifiedAt) told.push('Board told ' + stampWithZone(b.boardNotifiedAt) + '.')
      if (b.principalsNotifiedAt) told.push('People affected told ' + stampWithZone(b.principalsNotifiedAt) + '.')
      // Consent sends no Board time when the authority told was not
      // specifically the Data Protection Board.
      const note = told.length > 0 ? told.join(' ') : 'An authority or the people affected were told.'
      return { tone: 'crit', text: 'Confirmed and told (' + ref + ')', note }
    }
    case 'CLOSED':
      return { tone: 'neutral', text: 'Closed in ' + tool + ' (' + ref + ')' }
  }
}

// The words and tone for one incident's state in Consent, or null for a dash
// (not linked, not receiving incidents, or not shown to this role: the box
// says which, once).
export function consentStateOf(
  row: IncidentBreachRow,
  page: Pick<IncidentBreachPage, 'consent' | 'restricted'>,
): ConsentState | null {
  if (page.restricted) return null
  if (row.breach) return breachWords(row.breach)
  if (page.consent.state !== 'LINKED') return null
  switch (row.delivery) {
    case 'NOT_SENT':
      return {
        tone: 'warn',
        text: 'Not sent to Consent',
        note: 'Recorded before incident sharing was on. Edit the incident to send it.',
      }
    case 'PENDING':
      return { tone: 'info', text: 'Waiting to be sent to Consent' }
    case 'SENT':
      return { tone: 'info', text: 'Sent to Consent, waiting for the DPO' }
    case 'FAILED':
      return { tone: 'warn', text: 'Could not reach Consent; retrying' }
    case 'DEAD':
      return { tone: 'crit', text: 'Consent did not take it', note: RETRY_HINT }
    default:
      return null
  }
}

// A later change that is not reaching Consent, under an incident Consent has.
function updateNote(row: IncidentBreachRow, page: Pick<IncidentBreachPage, 'restricted'>): string | null {
  if (page.restricted || row.delivery !== 'SENT') return null
  if (row.updateDelivery === 'FAILED') return 'A later change is retrying.'
  if (row.updateDelivery === 'DEAD') return 'A later change did not reach Consent. ' + RETRY_HINT
  return null
}

function BoxHead(): JSX.Element {
  return (
    <div className="panel-head">
      <h3 id="vib-head" style={{ margin: 0 }}>
        Incidents and breach status
      </h3>
    </div>
  )
}

// The box, drawn from an answer. Exported for the tests.
export function IncidentBreachView({ page }: { page: IncidentBreachPage }): JSX.Element {
  const state = page.consent.state
  return (
    <section className="panel" aria-labelledby="vib-head">
      <BoxHead />
      {state === 'NOT_LINKED' && (
        <p className="muted">Not linked to Consent. FOCTTA Consent is not connected, so incidents are handled here only.</p>
      )}
      {state === 'CONNECTED_NO_FEED' && (
        <p className="note warn">
          Consent is connected but is not receiving vendor incidents yet. Ask the FOCTTA Consent administrator to turn
          on incident sharing with TPRM.
        </p>
      )}
      {page.restricted && <p className="muted">Breach status is not shown to your role.</p>}
      {page.incidents.length === 0 ? (
        <p className="muted">No incidents recorded for this vendor.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <caption className="vh">The vendor's incidents, newest first, with what became of each in Consent.</caption>
            <thead>
              <tr>
                <th>Incident</th>
                <th>Discovered</th>
                <th>Here</th>
                <th>In Consent</th>
              </tr>
            </thead>
            <tbody>
              {page.incidents.map((row) => {
                const s = consentStateOf(row, page)
                const later = updateNote(row, page)
                return (
                  <tr key={row.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <a className="mono" href={'#/incident/' + encodeURIComponent(row.id)}>
                        {row.reference}
                      </a>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{shortDate(row.discoveredAt)}</td>
                    <td>
                      <StatusChip domain="incident" code={row.status} />
                    </td>
                    <td>
                      {s ? <Status tone={s.tone}>{s.text}</Status> : <span className="muted">-</span>}
                      {s?.note && <div className="muted">{s.note}</div>}
                      {later && <div className="muted">{later}</div>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {page.more && <p className="muted">Showing the newest {page.limit}.</p>}
    </section>
  )
}

const YEAR_MS = 365 * 24 * 60 * 60 * 1000

// The risk input line, or null when there is nothing to say. Only incidents
// discovered in the last 12 months count; a confirmed breach outranks a
// suspected one. Exported for the tests.
export function breachRiskInput(
  page: IncidentBreachPage,
  now: Date = new Date(),
): { tone: 'stop' | 'warn'; row: IncidentBreachRow; text: string } | null {
  const recent = page.incidents.filter((r) => {
    const at = new Date(r.discoveredAt).getTime()
    return r.breach !== null && !Number.isNaN(at) && now.getTime() - at <= YEAR_MS
  })
  const confirmed = recent.filter((r) => CONFIRMED.has(r.breach!.status))
  const newestConfirmed = confirmed[0]
  if (newestConfirmed) {
    const row = newestConfirmed
    const b = row.breach!
    const facts = row.reference + ', ' + (b.breachReference ?? b.reportReference) + ', discovered ' + shortDate(row.discoveredAt)
    const text =
      confirmed.length === 1
        ? 'a vendor incident was confirmed as a personal data breach in ' + toolName(b, true) + ' (' + facts + ').'
        : confirmed.length +
          ' vendor incidents in the last 12 months were confirmed as personal data breaches in ' +
          toolName(b, true) +
          '. The newest: ' +
          facts +
          '.'
    return { tone: 'stop', row, text }
  }
  const suspected = recent.filter((r) => r.breach!.status === 'SUSPECTED')
  const newestSuspected = suspected[0]
  if (newestSuspected) {
    const row = newestSuspected
    const b = row.breach!
    const text =
      suspected.length === 1
        ? 'a vendor incident is a suspected breach in ' + toolName(b, true) + ' (' + row.reference + ').'
        : suspected.length + ' vendor incidents are suspected breaches in ' + toolName(b, true) + '. The newest: ' + row.reference + '.'
    return { tone: 'warn', row, text }
  }
  return null
}

// The risk line, drawn from an answer. Exported for the tests.
export function BreachRiskLine({ page, now }: { page: IncidentBreachPage; now?: Date }): JSX.Element | null {
  const input = breachRiskInput(page, now)
  if (!input) return null
  return (
    <p className={'note ' + input.tone} role="note">
      {/* b, not strong: a note's strong is a title on its own line, and this is one line. */}
      <b>Risk input:</b> {input.text} <span className="muted">Not counted in the score above.</span>
    </p>
  )
}

// Where the read stands.
export type BreachLoad = { state: 'loading' } | { state: 'failed' } | { state: 'ready'; page: IncidentBreachPage }

// One read of the box's answer. A failure is a state of its own, never "no
// incidents". Exported for the tests, which hand it a failing fetch.
export async function readIncidentBreach(
  vendorId: string,
  fetchPage: (vendorId: string) => Promise<IncidentBreachPage> = api.vendorIncidentBreach,
): Promise<BreachLoad> {
  try {
    return { state: 'ready', page: await fetchPage(vendorId) }
  } catch {
    return { state: 'failed' }
  }
}

function useIncidentBreach(vendorId: string): BreachLoad {
  const [load, setLoad] = useState<BreachLoad>({ state: 'loading' })
  useEffect(() => {
    let live = true
    setLoad({ state: 'loading' })
    void readIncidentBreach(vendorId).then((l) => {
      if (live) setLoad(l)
    })
    return () => {
      live = false
    }
  }, [vendorId])
  return load
}

// What the mounted box draws for each state of the read. Exported for the tests.
export function IncidentBreachBox({ load }: { load: BreachLoad }): JSX.Element | null {
  if (load.state === 'loading') return null
  if (load.state === 'failed') {
    return (
      <section className="panel" aria-labelledby="vib-head">
        <BoxHead />
        <p className="note warn">Could not read the incident and breach status. Reload to try again.</p>
      </section>
    )
  }
  return <IncidentBreachView page={load.page} />
}

// What the mounted risk line draws for each state of the read. Exported for the tests.
export function BreachRiskInput({ load, now }: { load: BreachLoad; now?: Date }): JSX.Element | null {
  if (load.state === 'loading') return null
  if (load.state === 'failed') {
    return (
      <p className="note warn" role="note">
        Could not read whether a vendor incident is a breach. Reload to try again.
      </p>
    )
  }
  return <BreachRiskLine page={load.page} now={now} />
}

export function VendorIncidentBreachBox({ vendorId }: { vendorId: string }): JSX.Element | null {
  return <IncidentBreachBox load={useIncidentBreach(vendorId)} />
}

export function VendorBreachRiskInput({ vendorId }: { vendorId: string }): JSX.Element | null {
  return <BreachRiskInput load={useIncidentBreach(vendorId)} />
}
